import ModalResponsive from '@/components/commons/modal-responsive';
import { ICollection } from '@/types/collection';
import { ExternalLinkIcon, PlusIcon, SearchIcon, Check } from 'lucide-react';
import Link from 'next/link';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useCollections } from '@/hooks/api/dashboard/colletions';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { cn } from '@/lib/utils';
import { FallbackImage } from '@/components/commons/fallback-image';

export interface ModalSelectCollectionProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  onSelect?: (collection: Partial<ICollection>) => void;
  initialValues?: Partial<ICollection>;
}

export default function ModalSelectCollection({
  openModal,
  setOpenModal,
  onSelect,
  initialValues,
}: ModalSelectCollectionProps) {
  const [keyword, setKeyword] = useState('');

  const q = useDebounceValue(keyword, 500);

  const { data: collections, isLoading } = useCollections({ page: 1, pageSize: 20, q });
  return (
    <ModalResponsive
      openModal={openModal}
      title='Select Collection'
      setOpenModal={setOpenModal}
      className='max-w-md'
    >
      <div className='px-6'>
        <Input
          name='keyword'
          type='text'
          size={100}
          placeholder='Search'
          leftContent={<SearchIcon className='mx-auto w-4' />}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>
      <div className='h-[600px] overflow-y-auto pb-4'>
        <Link
          href='#'
          target='_blank'
          className={cn(
            'flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-100'
          )}
        >
          <div className='flex items-center gap-x-4 text-primary'>
            <PlusIcon className='h-4 w-4' />
            Create Collection
          </div>
          <ExternalLinkIcon className='h-4 w-4 text-primary' />
        </Link>
        {isLoading ? (
          <div className='flex w-full items-center justify-center py-2'>
            <LoadingAnimationIcon />
          </div>
        ) : (
          collections?.map((collection) => {
            const selected = initialValues?.collection_id === collection.collection_id;
            return (
              <button
                key={collection.collection_id}
                onClick={() => {
                  onSelect?.(collection);
                  setOpenModal?.(false);
                }}
                className={cn('group flex w-full items-center justify-between px-6 py-2')}
              >
                <div className='flex items-center gap-x-2'>
                  <div className='h-[35px] w-[35px]'>
                    <FallbackImage
                      src={collection.img_url}
                      fallback='https://placehold.co/400?text=Image'
                      className='h-full w-full rounded object-cover'
                      alt={collection.collection_name}
                      width={30}
                      height={30}
                    />
                  </div>
                  <span className='truncate text-sm'>{collection.collection_name}</span>
                </div>
                {selected ? (
                  <Check className='h-4 w-4 text-primary' />
                ) : (
                  <Check className='h-4 w-4 opacity-0 duration-100 group-hover:opacity-100' />
                )}
              </button>
            );
          })
        )}
      </div>
    </ModalResponsive>
  );
}
